import React, { useState, useEffect } from 'react';
import { getApiUrl } from '@/utils/authUtils';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  Target, Flame, Snowflake, Trophy, CheckCircle2, 
  Calendar, TrendingUp, Sparkles
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import confetti from 'canvas-confetti';
import { apiFetch } from '@/hooks/useFetch';

const API_URL = getApiUrl();

// Fire confetti celebration
const celebrate = () => {
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: ['#00E5CC', '#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1']
  });
};

export const StreakGoalCard = () => {
  const { isSpanish } = useLanguage();
  const { user } = useAuth();
  const [goalData, setGoalData] = useState(null);
  const [freezeData, setFreezeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [newGoal, setNewGoal] = useState(3);
  const [goalType, setGoalType] = useState('phrases');

  useEffect(() => {
    if (user) {
      fetchGoalData();
      fetchFreezeData();
    }
    // Re-fetch when any phrase is tracked elsewhere in the app
    const onPhraseViewed = () => { if (user) fetchGoalData(); };
    window.addEventListener('phrase-viewed', onPhraseViewed);
    return () => window.removeEventListener('phrase-viewed', onPhraseViewed);
  }, [user]);

  const fetchGoalData = async () => {
    try {
      const { data, error } = await apiFetch('/api/friends/streak/goal');
      if (!error && data) {
        setGoalData(data);
        setNewGoal(data.daily_goal);
        setGoalType(data.goal_type);
      }
    } catch (error) {
      console.error('Failed to fetch goal:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFreezeData = async () => {
    try {
      const { data, error } = await apiFetch('/api/friends/streak/freezes');
      if (!error && data) {
        setFreezeData(data);
      }
    } catch (error) {
      console.error('Failed to fetch freezes:', error);
    }
  };

  const handleSetGoal = async () => {
    try {
      const { error } = await apiFetch('/api/friends/streak/goal', {
        method: 'PUT',
        body: { daily_goal: newGoal, goal_type: goalType }
      });

      if (!error) {
        toast.success(isSpanish ? '¡Meta actualizada!' : 'Goal updated!');
        setShowGoalDialog(false);
        fetchGoalData();
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error al actualizar' : 'Failed to update');
    }
  };

  const handleUseFreeze = async () => {
    try {
      const { error } = await apiFetch('/api/friends/streak/freeze/use', {
        method: 'POST'
      });

      if (!error) {
        toast.success(
          isSpanish 
            ? '❄️ ¡Congelación de racha activada!' 
            : '❄️ Streak freeze activated!'
        );
        fetchFreezeData();
      } else {
        toast.error(error || 'Failed to use freeze');
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error' : 'Failed to use freeze');
    }
  };

  // Show celebration when goal is met
  useEffect(() => {
    if (goalData?.goal_met && goalData?.percentage === 100) {
      celebrate();
    }
  }, [goalData?.goal_met]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="animate-pulse text-muted-foreground">
            {isSpanish ? 'Cargando...' : 'Loading...'}
          </div>
        </CardContent>
      </Card>
    );
  }

  const goalTypeLabels = {
    phrases: isSpanish ? 'frases' : 'phrases',
    activities: isSpanish ? 'actividades' : 'activities',
    minutes: isSpanish ? 'minutos' : 'minutes'
  };

  return (
    <div className="space-y-4">
      {/* Daily Goal Card */}
      <Card data-testid="streak-goal-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-[#00E5CC]" />
              {isSpanish ? 'Meta Diaria' : 'Daily Goal'}
            </div>
            <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="edit-goal-btn">
                  {isSpanish ? 'Editar' : 'Edit'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {isSpanish ? 'Establecer Meta Diaria' : 'Set Daily Goal'}
                  </DialogTitle>
                  <DialogDescription>
                    {isSpanish 
                      ? '¿Cuánto quieres lograr cada día?' 
                      : 'How much do you want to achieve each day?'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      {isSpanish ? 'Tipo de meta' : 'Goal type'}
                    </label>
                    <Select value={goalType} onValueChange={setGoalType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phrases">
                          {isSpanish ? 'Frases vistas' : 'Phrases viewed'}
                        </SelectItem>
                        <SelectItem value="activities">
                          {isSpanish ? 'Actividades completadas' : 'Activities completed'}
                        </SelectItem>
                        <SelectItem value="minutes">
                          {isSpanish ? 'Minutos activos' : 'Active minutes'}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      {isSpanish ? 'Meta diaria' : 'Daily goal'}
                    </label>
                    <Select 
                      value={newGoal.toString()} 
                      onValueChange={(v) => setNewGoal(parseInt(v))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 5, 7, 10, 15, 20].map((n) => (
                          <SelectItem key={n} value={n.toString()}>
                            {n} {goalTypeLabels[goalType]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={handleSetGoal} data-testid="save-goal-btn">
                    {isSpanish ? 'Guardar' : 'Save'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress Ring */}
          <div className="flex items-center gap-4">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle
                  cx="40"
                  cy="40"
                  r="35"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  className="text-muted/30"
                />
                <circle
                  cx="40"
                  cy="40"
                  r="35"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={`${(goalData?.percentage || 0) * 2.2} 220`}
                  strokeLinecap="round"
                  className={goalData?.goal_met ? "text-green-500" : "text-[#00E5CC]"}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                {goalData?.goal_met ? (
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                ) : (
                  <span className="text-lg font-bold">
                    {goalData?.percentage || 0}%
                  </span>
                )}
              </div>
            </div>
            <div className="flex-1">
              <p className="font-medium">
                {goalData?.current_progress || 0} / {goalData?.daily_goal || 3} {goalTypeLabels[goalData?.goal_type || 'phrases']}
              </p>
              <p className="text-sm text-muted-foreground">
                {goalData?.goal_met 
                  ? (isSpanish ? '🎉 ¡Meta completada!' : '🎉 Goal completed!')
                  : (isSpanish ? 'Progreso de hoy' : "Today's progress")}
              </p>
              {goalData?.streak_days_with_goal_met > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <Flame className="w-4 h-4 text-orange-500" />
                  <span className="text-sm text-orange-500 font-medium">
                    {goalData.streak_days_with_goal_met} {isSpanish ? 'días seguidos' : 'days in a row'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Goal Stats */}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t">
            <div className="text-center p-2 bg-muted/30 rounded-lg">
              <Trophy className="w-4 h-4 mx-auto text-yellow-500 mb-1" />
              <p className="text-lg font-bold">{goalData?.longest_goal_streak || 0}</p>
              <p className="text-xs text-muted-foreground">
                {isSpanish ? 'Mejor racha' : 'Best streak'}
              </p>
            </div>
            <div className="text-center p-2 bg-muted/30 rounded-lg">
              <Calendar className="w-4 h-4 mx-auto text-blue-500 mb-1" />
              <p className="text-lg font-bold">{goalData?.streak_days_with_goal_met || 0}</p>
              <p className="text-xs text-muted-foreground">
                {isSpanish ? 'Días completados' : 'Days completed'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Streak Freeze Card */}
      <Card data-testid="streak-freeze-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Snowflake className="h-5 w-5 text-blue-400" />
            {isSpanish ? 'Congelación de Racha' : 'Streak Freeze'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {isSpanish 
              ? 'Protege tu racha si te pierdes un día. ¡Tienes hasta 2 congelaciones por mes!'
              : "Protect your streak if you miss a day. You get up to 2 freezes per month!"}
          </p>
          
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="flex gap-1">
                {[...Array(2)].map((_, i) => (
                  <span 
                    key={i}
                    className={`text-2xl ${
                      i < (freezeData?.available_freezes || 0) 
                        ? '' 
                        : 'opacity-30 grayscale'
                    }`}
                    role="img"
                    aria-label="snowman"
                  >
                    ⛄
                  </span>
                ))}
              </div>
              <div>
                <p className="font-medium">
                  {freezeData?.available_freezes || 0} / {freezeData?.max_freezes || 2}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isSpanish ? 'disponibles' : 'available'}
                </p>
              </div>
            </div>
            {/* Manual "Use freeze" button removed per user request — freezes
                now apply automatically when a streak day is missed. The
                StreakFreezeNotification modal shows up if one was used. */}
            <span className="text-xs text-muted-foreground italic" data-testid="freeze-auto-note">
              {isSpanish ? 'Se usa automáticamente' : 'Used automatically'}
            </span>
          </div>

          {freezeData?.active_freeze && (
            <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                {isSpanish 
                  ? '¡Por favor no nos abandones! 👻' 
                  : "Please don't ghost us! 👻"}
              </p>
              <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                {isSpanish 
                  ? 'Tu racha está salvada por ahora, ¡pero solo tú puedes mantenerla!' 
                  : 'Your streak was saved for now, but only you can keep it going!'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StreakGoalCard;
